import { PropsWithoutRef } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { t as txt } from 'i18next';
import { dateFormats, sensorUnits, tooltipBorderColor, colors } from 'commons/src/constants';
import { timeFormatter } from 'commons/src/features/devicePage/graphDataFunctions';
import { dateTime, dayDate, setXAxisLabelFormat, yVal } from 'commons/src/features/devicePage/SensorConfig';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { SelectedPeriod } from 'commons/src/models/commonTypeScript';

declare module 'highcharts' {
    interface Point {
        highlight?: () => void;
    }
}

export const chartOnMouseOver = (e: MouseEvent | TouchEvent, mouseIsOut: boolean): void => {
    Highcharts.charts.forEach(chart => {
        if (!chart) return;
        const event = chart.pointer?.normalize(e as PointerEvent);
        if (!event) return;
        const point = chart.series?.flatMap(series => series.searchPoint(event, true)).find(p => !!p);
        if (point) {
            if (mouseIsOut) {
                chart.tooltip.hide();
            } else {
                point.highlight?.();
            }
        }
    });
};

Highcharts.Pointer.prototype.reset = (): void => {
    Highcharts.charts.forEach(chart => chart?.tooltip.hide());
};
Highcharts.Point.prototype.highlight = function highlight(): void {
    this.onMouseOver();
};

export type SpaceGraphData = {
    data: [number, number | null][];
    id: string;
    name: string;
    color?: string;
    type?: string;
    lineWidth?: number;
    showInLegend?: boolean;
    opacity?: number;
    yAxis?: number;
    dashStyle?: string;
    zones?: { value?: number; color: string }[];
};
type Props = {
    data: SpaceGraphData[];
    usageHoursPlotBands: { color: string; from: number; to: number }[];
    selectedInterval: SelectedPeriod;
    dateFormat: keyof typeof dateFormats;
    sensorUnit: keyof typeof sensorUnits;
    graphStartAndEnd: { max: number; min: number };
    compareSensorUnit?: keyof typeof sensorUnits;
    compareSensorType?: SensorTypes;
    chartHeight: number;
};

const mainChartId = 'spaceGraph';
export const rangeSeriesName = 'rangeSeries';

const getTooltipSensorValues = (
    {
        series: { name, options: seriesOptions },
        point: { y, options },
    }: {
        series: Highcharts.Series;
        point: Highcharts.Point;
    },
    sensorUnit: keyof typeof sensorUnits,
    compareUnit?: keyof typeof sensorUnits,
    compareSensorType?: SensorTypes
): { id: string; name: string; value: string } => {
    const seriesId = seriesOptions.id as string;
    const sensor = seriesId.split('-')[0];

    const indexOfFirstDash = seriesId.indexOf('-');

    let segmentId = seriesId.substring(indexOfFirstDash + 1);
    const isOutdoor = segmentId.includes('outdoor');
    if (!isOutdoor) {
        const indexOfLastDash = segmentId.lastIndexOf('-');
        segmentId = segmentId.substring(0, indexOfLastDash);
    }

    let value;
    const unit = compareSensorType === sensor && compareUnit ? compareUnit : sensorUnit;
    if (unit === 'occ') {
        value =
            name === rangeSeriesName
                ? `${options.low} - ${options.high} ${txt(`SensorUnit.${unit}` || '')}`
                : `${y} ${txt(`SensorUnit.${unit}` || '')}`;
    } else if (name === rangeSeriesName) {
        value =
            options.high !== null && options.low !== null
                ? `Min: ${yVal(options.low, unit)}, Max: ${yVal(options.high, unit)}`
                : '';
    } else if (unit === 'minutes') {
        value = `${timeFormatter(y)}`;
    } else {
        value = `${yVal(y, unit)} ${sensorUnits[unit]}`;
    }
    const dontShowSensor = !compareUnit || isOutdoor;

    return {
        id: segmentId,
        name,
        value: dontShowSensor ? value : `${txt(sensor)}: ${value}`,
    };
};

const getTooltipText = (
    points: Highcharts.TooltipFormatterContextObject[],
    sensorUnit: keyof typeof sensorUnits,
    compareUnit?: keyof typeof sensorUnits,
    compareSensorType?: SensorTypes
): string[] => {
    const getPointData =
        points?.map(point => getTooltipSensorValues(point, sensorUnit, compareUnit, compareSensorType)) || [];
    const pointText = getPointData.reduce((acc, pointData) => {
        const deviceInState = acc[pointData.id];
        if (deviceInState) {
            acc[pointData.id] = { name: deviceInState.name, value: `${deviceInState.value}<br/>${pointData.value}` };
        } else {
            acc[pointData.id] = { name: pointData.name, value: pointData.value };
        }
        return acc;
    }, {} as { [id: string]: { name: string; value: string } });
    const indexOfOutdoor = Object.keys(pointText).findIndex(id => id.includes('outdoor'));
    return Object.values(pointText).map(({ name, value }, i) => {
        return compareUnit && indexOfOutdoor !== i ? `${name}<br/>${value}<br/>` : `${name}: ${value}<br/>`;
    });
};

const yAxisFormatter = (value: number, sensorUnit: keyof typeof sensorUnits): string => {
    if (sensorUnit === 'minutes') return timeFormatter(value);
    const yValue = Math.round(value * 10) / 10;
    return yValue % 1 === 0 || sensorUnit === sensorUnits.bq
        ? Highcharts.numberFormat(yValue, 0)
        : Highcharts.numberFormat(yValue, 1);
};

const spaceGraphConfig = ({
    data,
    usageHoursPlotBands,
    selectedInterval,
    dateFormat,
    sensorUnit,
    graphStartAndEnd,
    compareSensorUnit,
    compareSensorType,
    chartHeight,
}: Props): PropsWithoutRef<HighchartsReact.Props> => ({
    chart: {
        backgroundColor: null,
        borderWidth: 0,
        type: 'line',
        spacingBottom: 5,
        height: chartHeight,
        style: {
            fontFamily: 'OpenSans-Regular',
        },
    },
    id: mainChartId,
    title: {
        text: '',
    },
    credits: {
        enabled: false,
    },
    xAxis: {
        tickLength: 0,
        tickPixelInterval: 130,
        plotBands: usageHoursPlotBands,
        lineColor: 'transparent',
        min: graphStartAndEnd.min,
        max: graphStartAndEnd.max,
        labels: {
            formatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
                const { value } = this;
                return Highcharts.dateFormat(setXAxisLabelFormat(selectedInterval, dateFormat), value as number);
            },
        },
        events: {
            setExtremes(this: Highcharts.Chart, e: Highcharts.AxisSetExtremesEventObject): void {
                Highcharts.charts.forEach(chart => {
                    if (chart && chart !== this && chart?.xAxis[0]) {
                        chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, { trigger: 'syncExtremes' });
                    }
                });
            },
        },
    },
    yAxis: [
        {
            title: { enabled: false },
            min: data.length === 0 ? 0 : undefined,
            endOnTick: false,
            startOnTick: false,
            gridLineWidth: 0,
            tickPixelInterval: 40,
            labels: {
                formatter(this: { value: number }): string {
                    const { value } = this;
                    return yAxisFormatter(value, sensorUnit);
                },
            },
        },
        {
            gridLineWidth: 0,
            title: {
                text: null,
            },
            labels: {
                formatter(this: { value: number }): string {
                    const { value } = this;
                    return compareSensorUnit ? yAxisFormatter(value, compareSensorUnit) : '';
                },
            },
            opposite: true,
        },
    ],
    tooltip: {
        borderColor: tooltipBorderColor,
        style: {
            color: colors.white,
        },
        enabled: true,
        hideDelay: 0,
        shared: true,
        formatter(this: Highcharts.TooltipFormatterContextObject): string {
            const { resolution } = selectedInterval;
            const { x, points } = this;
            const seriesValues = getTooltipText(points || [], sensorUnit, compareSensorUnit, compareSensorType);
            return [
                dayDate(x as number, dateFormat, resolution),
                dateTime(x as number, dateFormat, resolution),
                ...seriesValues,
            ].join('<br/>');
        },
    },
    plotOptions: {
        series: {
            animation: { duration: 600, direction: 'bottom' },
            showInLegend: data.length > 0,
            lineWidth: 2,
            shadow: false,
            marker: {
                enabled: false,
                symbol: 'circle',
            },
            states: {
                hover: { lineWidthPlus: 0 },
            },
        },
        column: {
            borderRadius: 4,
        },
    },
    legend: {
        symbolWidth: 20,
        labelFormatter: function labelFormatter(): string {
            const seriesId = this.userOptions.id;
            const seriesSensor = seriesId.split('-')[0];
            const isOutdoor = seriesId.includes('outdoor');
            return compareSensorUnit && !isOutdoor ? `${this.name} (${txt(seriesSensor)})` : this.name;
        },
    },
    series:
        data.length > 0
            ? data.map(seriesData => ({
                  type: 'line',
                  color: colors.greyRiverBed,
                  connectNulls: false,
                  ...seriesData,
              }))
            : [{ data: [], id: '', name: '', color: '' }],
    legendColor: colors.greyRiverBed,
});

export default spaceGraphConfig;
